import React, { useEffect, useState } from "react";
import { Box, Typography, useMediaQuery, Grid, Skeleton, Container, Divider } from "@mui/material";
import { useNavigate, useNavigationType } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import config from "../../config/config";
import Header from "../../Components/Common/Header";
import Footer from "../../Components/Common/Footer";

const BrandsPage = () => {
  const smallScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (navigationType !== "POP") {
      window.scrollTo(0, 0);
    }
    const fetchBrands = async () => {
      try {
        const response = await axios.get(`${config.backendUrl}/inventory/all_brands`);
        setBrands(response.data?.results?.brands || []);
      } catch (error) {
        console.error("Error fetching all brands:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchBrands();
  }, []);

  const groupedBrands = brands.reduce((acc, brandName) => {
    let firstChar = brandName.charAt(0).toUpperCase();
    if (!/[A-Z]/.test(firstChar)) {
      firstChar = "#";
    }
    if (!acc[firstChar]) acc[firstChar] = [];
    acc[firstChar].push(brandName);
    return acc;
  }, {});

  const sortedKeys = Object.keys(groupedBrands).sort((a, b) => {
    if (a === "#") return -1;
    if (b === "#") return 1;
    return a.localeCompare(b);
  });

  // Animation Variants
  const containerVariants = {
    hidden: {},
    visible: {
      transition: { staggerChildren: 0.08 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 15 },
    visible: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 80, damping: 15 } }
  };

  const headerVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
  };

  return (
    <Box sx={{ background: "#FFFFFF", minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <Header />
      
      <Box sx={{ flex: 1, py: { xs: 6, md: 10 }, background: "linear-gradient(180deg, #FFFFFF 0%, #FAFAFA 50%, #FFFFFF 100%)" }}>
        <Container maxWidth="lg">
          <Box 
            component={motion.div}
            initial="hidden"
            animate="visible"
            variants={headerVariants}
            sx={{ mb: { xs: 6, md: 10 }, textAlign: "center" }}
          >
            <Typography
              variant="h1"
              sx={{
                fontFamily: "'Lora', serif",
                fontSize: { xs: "36px", md: "52px" },
                color: "#111",
                mb: 2,
                fontWeight: 500,
                letterSpacing: "-0.02em",
                textTransform: "capitalize",
              }}
            >
              All <Box component="span" sx={{ fontStyle: "italic", color: "#FF6B00" }}>Brands</Box>
            </Typography>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: { xs: "flex-start", md: "center" },
                backgroundColor: "rgba(255, 107, 0, 0.06)",
                border: "1px solid rgba(255, 107, 0, 0.2)",
                borderRadius: { xs: "16px", md: "100px" },
                px: { xs: 2.5, md: 3 },
                py: { xs: 1, md: 1.5 },
                mx: "auto",
              }}
            >
              <Box sx={{ display: "flex", mt: { xs: "2px", md: 0 }, flexShrink: 0, mr: 1.5 }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#FF6B00" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                  <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
                </svg>
              </Box>
              <Typography
                sx={{
                  fontFamily: "Poppins-Medium",
                  fontSize: { xs: "13px", md: "15px" },
                  color: "#FF6B00",
                  letterSpacing: "0.5px",
                  textAlign: "center",
                }}
              >
                Explore our complete directory of premium brands
              </Typography>
            </Box>
          </Box>

          {loading ? (
            <Box sx={{ maxWidth: "800px", mx: "auto" }}>
              {Array.from(new Array(5)).map((_, index) => (
                <Box key={index} sx={{ mb: 6 }}>
                  <Skeleton variant="text" width="60px" height="60px" sx={{ mb: 2 }} />
                  <Divider sx={{ mb: 3 }} />
                  <Grid container spacing={3}>
                    {Array.from(new Array(8)).map((_, idx) => (
                      <Grid item xs={6} sm={4} md={3} key={idx}>
                        <Skeleton variant="text" width="80%" height="24px" />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              ))}
            </Box>
          ) : (
            <Box
              component={motion.div}
              initial="hidden"
              animate="visible"
              variants={containerVariants}
              sx={{ maxWidth: "1000px", mx: "auto" }}
            >
              {sortedKeys.map((letter) => (
                <Box
                  component={motion.div}
                  variants={itemVariants}
                  key={letter}
                  sx={{ mb: { xs: 6, md: 8 } }}
                >
                  <Box sx={{ display: "flex", alignItems: "baseline", mb: 4 }}>
                    <Typography
                      sx={{
                        fontFamily: "'Lora', serif",
                        fontSize: { xs: "32px", md: "46px" },
                        fontWeight: 500,
                        color: "#111",
                        width: { xs: "50px", md: "70px" },
                        lineHeight: 1
                      }}
                    >
                      {letter}
                    </Typography>
                    <Divider sx={{ flex: 1, borderColor: "rgba(0,0,0,0.06)" }} />
                  </Box>
                  
                  <Grid container spacing={{ xs: 2, md: 4 }} sx={{ pl: { xs: 0, sm: "70px" } }}>
                    {groupedBrands[letter].map((brandName, index) => (
                      <Grid item xs={12} sm={6} md={4} key={`${brandName}-${index}`}>
                        <Box
                          onClick={() => {
                            const slug = brandName.replace(/\s+/g, "-");
                            navigate(`/view-all/${encodeURIComponent(slug)}`);
                          }}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            py: 1.5,
                            borderBottom: "1px solid #EAEAEA",
                            cursor: "pointer",
                            transition: "all 0.3s ease",
                            "&:hover": {
                              borderColor: "#FF6B00",
                            },
                            "&:hover .brand-text": {
                              color: "#FF6B00",
                              transform: "translateX(8px)"
                            },
                            "&:hover .brand-icon": {
                              opacity: 1,
                              transform: "translateX(-4px)"
                            }
                          }}
                        >
                          <Typography
                            className="brand-text"
                            sx={{
                              fontFamily: "Poppins-Medium",
                              fontSize: { xs: "14px", md: "15px" },
                              color: "#444",
                              transition: "all 0.3s ease",
                            }}
                          >
                            {brandName}
                          </Typography>
                          <svg className="brand-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#FF6B00" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0, transform: "translateX(-16px)", transition: "all 0.3s ease" }}>
                            <line x1="5" y1="12" x2="19" y2="12"></line>
                            <polyline points="12 5 19 12 12 19"></polyline>
                          </svg>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              ))}
            </Box>
          )}

          {!loading && brands.length === 0 && (
            <Box sx={{ textAlign: "center", py: 10 }}>
              <Typography sx={{ fontFamily: "Poppins-Medium", color: "#666", fontSize: "18px" }}>
                No brands found.
              </Typography>
            </Box>
          )}
        </Container>
      </Box>

      <Footer />
    </Box>
  );
};

export default BrandsPage;
